import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { supabase } from "../lib/supabase";

// ---------------------------------------------------------------------------
// CommissionAddendumGate — Jul 16, 2026
//
// The new commission structure (per-deal RM rate) takes effect for install
// agreements signed on/after Mon Jul 20, 2026. RM / TL / ASM must authorize a
// commission-structure addendum before continuing to use the CRM:
//   · Appears Monday morning (Jul 20, 00:00 ET) as a dismissible full-screen
//     prompt — "Remind me later" available until 10:00 AM ET.
//   · From 10:00 AM ET it is a HARD BLOCK: no dismiss, must authorize.
// Authorization = checkbox + button; identity is the Google-authenticated
// session (per Greg — no signature capture). Recorded once per member in
// team_acknowledgments (append-only, ack_key below) WITH a generated PDF
// stored next to their IC agreement (lib/commissionAddendumPDF.ts — the
// addendum text there must stay in sync with the JSX below). Mounted globally in
// Layout so it covers every staff page including /canvas (z-[200] sits above
// the Canvas full-screen map). /crew is a separate shell and is NOT gated.
// ---------------------------------------------------------------------------

const ACK_KEY = "comm_structure_2026_07";
const GATED_ROLES = new Set(["restore_manager", "team_lead", "asm"]);
const SHOW_AT_MS = Date.parse("2026-07-20T04:00:00Z"); // Mon Jul 20, 00:00 ET (EDT)
const BLOCK_AT_MS = Date.parse("2026-07-20T14:00:00Z"); // Mon Jul 20, 10:00 AM ET

export default function CommissionAddendumGate() {
  const { member } = useAuth();
  const gated = !!member && GATED_ROLES.has(member.role);

  // null = not yet checked. Only queried once we're past SHOW_AT for a gated role.
  const [acked, setAcked] = useState<boolean | null>(null);
  const [dismissed, setDismissed] = useState(false); // per-mount, pre-10am only
  const [agree, setAgree] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [nowMs, setNowMs] = useState(() => Date.now());

  const live = nowMs >= SHOW_AT_MS;

  // Minute tick so the Monday-morning appearance and the 10 AM hard-block flip
  // without a reload for someone who left a tab open.
  useEffect(() => {
    const t = setInterval(() => setNowMs(Date.now()), 60_000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!gated || !live || !member) return;
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from("team_acknowledgments")
        .select("id")
        .eq("member_id", member.id)
        .eq("ack_key", ACK_KEY)
        .maybeSingle();
      // Fail open on read errors — never lock someone out over a flaky fetch;
      // the gate re-checks on next mount / minute tick.
      if (!cancelled) setAcked(error ? true : !!data);
    })();
    return () => { cancelled = true; };
  }, [gated, live, member]);

  if (!gated || !live) return null;
  if (acked === null || acked === true) return null;
  const hardBlock = nowMs >= BLOCK_AT_MS;
  if (!hardBlock && dismissed) return null;

  async function authorize() {
    if (!agree || !member || saving) return;
    setSaving(true);
    setErr(null);

    // IC-agreement parity (Greg, Jul 16): generate the authorized addendum PDF
    // and store it next to the member's IC agreement BEFORE writing the ack row,
    // so pdf_url/pdf_path ride on the insert (the table is append-only — no
    // UPDATE policy). PDF failure never blocks authorization: the timestamped
    // row is the source of truth; the PDF is the paper copy.
    const recordId = crypto.randomUUID();
    const authorizedAt = new Date().toISOString();
    let pdfUrl: string | null = null;
    let pdfPath: string | null = null;
    try {
      const { generateCommissionAddendumPDF } = await import("../lib/commissionAddendumPDF");
      const blob = await generateCommissionAddendumPDF(
        { full_name: member.full_name, email: member.email, role: member.role },
        authorizedAt,
        recordId,
      );
      const path = `ic-agreements/${member.id}/${Date.now()}-commission-addendum-2026-07.pdf`;
      const { error: upErr } = await supabase.storage
        .from("deal-documents")
        .upload(path, blob, { contentType: "application/pdf", upsert: false });
      if (upErr) throw upErr;
      pdfPath = path;
      const { data: signed } = await supabase.storage
        .from("deal-documents")
        .createSignedUrl(path, 31536000); // 1 year, same as the IC agreement
      pdfUrl = signed?.signedUrl ?? null;
    } catch (pdfErr) {
      console.error("Commission addendum PDF failed (authorization proceeds):", pdfErr);
      pdfUrl = null;
      pdfPath = null;
    }

    const { error } = await supabase
      .from("team_acknowledgments")
      .insert({ id: recordId, member_id: member.id, ack_key: ACK_KEY, acknowledged_at: authorizedAt, pdf_url: pdfUrl, pdf_path: pdfPath });
    // 23505 = unique violation → already acknowledged (double-tap / second tab).
    if (error && error.code !== "23505") {
      setErr(error.message);
      setSaving(false);
      return;
    }
    setSaving(false);
    setAcked(true);
  }

  return (
    <div className="fixed inset-0 z-[200] bg-slate-900/80 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[88vh] overflow-y-auto">
        <div className="px-6 pt-6 pb-4 border-b border-slate-200">
          <div className="text-[11px] font-bold uppercase tracking-wide text-bardin-orange">
            Action required{hardBlock ? "" : " by 10:00 AM"}
          </div>
          <h1 className="text-lg font-semibold text-bardin-ink mt-1">
            Commission Structure Addendum
          </h1>
          <p className="text-xs text-slate-500 mt-0.5">
            Effective for install agreements signed on or after Monday, July 20, 2026
          </p>
        </div>

        <div className="px-6 py-4 text-sm text-slate-700 space-y-3">
          <p>
            Per your Independent Contractor Agreement with Peach Duck Contracting Company
            d/b/a Bardin Roofing, the commission structure is amended as follows for all
            deals whose <strong>install agreement is signed on or after July 20, 2026</strong>.
            Deals signed before that date remain on the prior structure.
          </p>
          <ol className="list-decimal pl-5 space-y-2">
            <li>
              The Restore Manager commission rate is set <strong>per deal</strong>:
              <ul className="list-disc pl-5 mt-1 space-y-1">
                <li><strong>12%</strong> — commissionable revenue of $20,000 or more, no credits on the deal (full insurance scope), and a verified 5-star customer review.</li>
                <li><strong>11%</strong> — commissionable revenue of $20,000 or more, credits of $500 or less, and a verified 5-star customer review.</li>
                <li><strong>10%</strong> — all other deals.</li>
              </ul>
            </li>
            <li>
              <strong>"Credits"</strong> means miscellaneous credits plus non-collect revenue
              on the deal. ACV credits do <strong>not</strong> count toward these thresholds.
            </li>
            <li>
              Deals carrying credits of <strong>$501 or more</strong> pay at 10%, and the
              commission is <strong>paid upon closure</strong> — released only after the
              deal's balance is collected in full.
            </li>
            <li>
              5-star reviews are verified by management. If a deal is paid at 10% before its
              review is verified and the review is verified before the deal closes, the
              difference is paid on the next payout.
            </li>
            <li>
              Team members who have already <strong>achieved a higher commission
              percentage</strong> (currently 12%) keep that achieved rate on all of their
              own deals in place of the tiers in item 1; only item 3 applies to them.
            </li>
            <li>
              On split deals, the deal's size, credits, and review set the tier, and each
              rep's half is rated under their own classification.
            </li>
            <li>
              The existing non-collect deduction policy (overage above $500 deducted from RM
              and TL commission) remains in effect and applies in addition to the rates above.
            </li>
            <li>
              Team Lead and ASM overrides, RS pool, retail commission, and canvasser program
              terms are unchanged.
            </li>
          </ol>
          <p className="text-xs text-slate-500">
            Your authorization is recorded to your account with a timestamp. Questions —
            talk to Greg or Chad before authorizing.
          </p>
        </div>

        <div className="px-6 pb-6 pt-2 border-t border-slate-200">
          <label className="flex items-start gap-2.5 text-sm text-slate-800 cursor-pointer">
            <input
              type="checkbox"
              checked={agree}
              onChange={(e) => setAgree(e.target.checked)}
              className="mt-0.5 w-4 h-4 accent-bardin-orange"
            />
            <span>
              I, <strong>{member?.full_name}</strong>, have read and agree to the commission
              structure described above, effective for install agreements signed on or after
              July 20, 2026.
            </span>
          </label>
          {err && (
            <div className="mt-2 text-xs text-red-600 bg-red-50 rounded px-2 py-1.5">
              Couldn&apos;t save: {err} — check your connection and try again.
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2 mt-3">
            <button
              onClick={authorize}
              disabled={!agree || saving}
              className="px-5 py-2 text-sm font-semibold rounded-lg bg-bardin-orange text-white hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {saving ? "Saving…" : "Authorize"}
            </button>
            {!hardBlock && (
              <button
                onClick={() => setDismissed(true)}
                className="px-4 py-2 text-sm font-medium rounded-lg bg-white border border-slate-300 text-slate-600 hover:bg-slate-50"
              >
                Remind me later
              </button>
            )}
            <span className="text-[11px] text-slate-400 ml-auto">
              {hardBlock
                ? "Authorization is required to continue using the CRM."
                : "Becomes required at 10:00 AM ET today."}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
